import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { Slider } from '@flipster/react/design-system-legacy/slider';
import { Badge } from '@flipster/react/design-system/badge';
import { Explain } from '@flipster/react/design-utils/explain';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

import { NumberInputWithSuffix } from '../../../input/number-input-with-suffix';
import { instrumentBunja } from '../../state/base';
import { tpslModalInputBunja } from '../state';
import { tpslModalValidationBunja } from '../validation';

export const SetQuantitySection = () => {
  const {
    tradeQtyPrecisionAtom,
    positionUnitAtom,
    quoteCurrencyAtom,
    quoteCurrencyPrecisionAtom,
    tpslAmountWarningMessageAtom,
    partialTpslSliderErrorMessageAtom,
    partialQtyAtom,
    partialQtyPercentAtom,
    partialQtyUsdtAtom,
    changeQtyPercent,
    changeQty,
  } = useBunja(setQuantitySectionBunja);
  const tradeQtyPrecision = useAtomValue(tradeQtyPrecisionAtom);
  const positionUnit = useAtomValue(positionUnitAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);
  const quoteCurrencyPrecision = useAtomValue(quoteCurrencyPrecisionAtom);
  const tpslAmountWarningMessage = useAtomValue(tpslAmountWarningMessageAtom);
  const partialTpslSliderErrorMessage = useAtomValue(partialTpslSliderErrorMessageAtom);

  const partialQty = useAtomValue(partialQtyAtom);
  const partialQtyPercent = useAtomValue(partialQtyPercentAtom);
  const partialQtyUsdt = useAtomValue(partialQtyUsdtAtom);

  return (
    <section
      className={stack({
        px: 16,
        py: 12,
        rounded: 8,
        bg: 'background.default.level-1',
      })}
    >
      <div className={stack({ pb: 8, gap: 6 })}>
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
          <T id='Autogen_common.r251126.s_Set_quantity' />
        </p>
        <div className={hstack({ justify: 'space-between', alignItems: 'flex-end' })}>
          <div className={stack({ gap: 4 })}>
            <NumberInputWithSuffix
              precision={tradeQtyPrecision}
              value={partialQty}
              onChange={changeQty}
              className={hstack({ textStyle: 'Title/S/Monospace', gap: 4, alignItems: 'flex-end' })}
              suffix={<div className={css({ textStyle: 'Text/L/Strong' })}>{positionUnit}</div>}
            />
            <div className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3' })}>
              <span>≈ </span>
              <Formatted.Number value={String(partialQtyUsdt)} precision={quoteCurrencyPrecision} toFixed />
              <span> {quoteCurrency}</span>
            </div>
          </div>
          <Badge type='tint' color='neutral' css={{ color: 'content.default.level-5' }}>
            <Formatted.Percent value={partialQtyPercent} />
          </Badge>
        </div>
      </div>
      <Slider
        css={{ my: 12 }}
        value={partialQtyPercent}
        onChange={changeQtyPercent}
        min={0}
        max={100}
        step={1}
        marks={[0, 25, 50, 75, 100]}
        getLabel={(mark) => `${mark}%`}
      />
      <Explain.Error>{partialTpslSliderErrorMessage}</Explain.Error>
      <Explain.Warning>{tpslAmountWarningMessage}</Explain.Warning>
    </section>
  );
};

const setQuantitySectionBunja = bunja(() => {
  const { tradeQtyPrecisionAtom, positionUnitAtom, quoteCurrencyAtom, quoteCurrencyPrecisionAtom } =
    bunja.use(instrumentBunja);
  const { partialQtyAtom, partialQtyPercentAtom, partialQtyUsdtAtom, changeQtyPercent, changeQty } =
    bunja.use(tpslModalInputBunja);
  const { tpslAmountWarningMessageAtom, partialTpslSliderErrorMessageAtom } = bunja.use(tpslModalValidationBunja);

  return {
    tradeQtyPrecisionAtom,
    positionUnitAtom,
    quoteCurrencyAtom,
    quoteCurrencyPrecisionAtom,
    tpslAmountWarningMessageAtom,
    partialTpslSliderErrorMessageAtom,
    partialQtyAtom,
    partialQtyPercentAtom,
    partialQtyUsdtAtom,
    changeQtyPercent,
    changeQty,
  };
});
setQuantitySectionBunja.debugLabel = 'setQuantitySectionBunja';
